type CacheEntry = { data: unknown; expiresAt: number }
type InflightEntry = { promise: Promise<unknown>; subscribers: number }

const resultCache = new Map<string, CacheEntry>()
const inflightCache = new Map<string, InflightEntry>()
const fetcherKeys = new WeakMap<object, string>()
let nextFetcherKey = 0
const MAX_CACHE_ENTRIES = 200

export const REPORT_QUERY_CACHE_TTL_MS = 180_000

const stableValue = (key: string, value: unknown): unknown =>
  key === 'referenceDate' && typeof value === 'string'
    ? value.slice(0, 10)
    : value

const stableObject = (
  value: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.keys(value)
      .sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => [key, stableValue(key, value[key])]),
  )

export function buildReportQueryCacheKey(
  fetcher: object,
  params: Record<string, unknown>,
): string {
  let fetcherKey = fetcherKeys.get(fetcher)
  if (!fetcherKey) {
    fetcherKey = `report-fetch-${nextFetcherKey++}`
    fetcherKeys.set(fetcher, fetcherKey)
  }
  const tenantId = localStorage.getItem('tenant_id') ?? ''
  return `${fetcherKey}|${tenantId}|${JSON.stringify(stableObject(params))}`
}

export function readReportQueryCache<T>(key: string): T | undefined {
  const entry = resultCache.get(key)
  if (!entry) return undefined
  if (entry.expiresAt <= Date.now()) {
    resultCache.delete(key)
    return undefined
  }
  resultCache.delete(key)
  resultCache.set(key, entry)
  return entry.data as T
}

export function writeReportQueryCache(key: string, data: unknown): void {
  resultCache.delete(key)
  resultCache.set(key, {
    data,
    expiresAt: Date.now() + REPORT_QUERY_CACHE_TTL_MS,
  })
  while (resultCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = resultCache.keys().next().value as string | undefined
    if (!oldestKey) break
    resultCache.delete(oldestKey)
  }
}

export function getReportQueryInflight<T>(key: string): Promise<T> | undefined {
  return inflightCache.get(key)?.promise as Promise<T> | undefined
}

export function joinReportQueryInflight(key: string): boolean {
  const entry = inflightCache.get(key)
  if (!entry) return false
  entry.subscribers += 1
  return true
}

export function setReportQueryInflight<T>(
  key: string,
  promise: Promise<T>,
): Promise<T> {
  inflightCache.set(key, { promise, subscribers: 1 })
  void promise
    .finally(() => {
      if (inflightCache.get(key)?.promise === promise) inflightCache.delete(key)
    })
    .catch(() => undefined)
  return promise
}

export function leaveReportQueryInflight(
  key: string,
  promise: Promise<unknown>,
): boolean {
  const entry = inflightCache.get(key)
  if (!entry || entry.promise !== promise) return false
  entry.subscribers = Math.max(0, entry.subscribers - 1)
  // Do not evict or abort when the last component leaves. React effects may
  // restart during toolbar/bootstrap updates or Strict Mode; the settled result
  // still warms the short-lived report cache. setReportQueryInflight removes the
  // entry when the promise actually settles.
  return false
}

export function clearReportQueryInflight(
  key: string,
  promise: Promise<unknown>,
): void {
  if (inflightCache.get(key)?.promise === promise) inflightCache.delete(key)
}

export function clearReportQueryCacheForTests(): void {
  resultCache.clear()
  inflightCache.clear()
}
